import { useAuth } from "@/hooks/use-auth-simple";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lightbulb, ArrowLeft, FileText, Search, Users, BarChart3, Zap, Target, Workflow, Layers, GitBranch } from "lucide-react";
import { Link, Redirect, useRoute } from "wouter";
import { Badge } from "@/components/ui/badge";

type SafeUser = {
  id: string;
  username: string;
  isAdmin?: boolean;
  is_admin?: boolean;
  department?: string;
};

type Project = {
  id: string;
  title: string;
  description: string;
  status: 'planning' | 'proposed' | 'in-development' | 'on-hold';
  category: string;
  icon: React.ReactNode;
  linkTo?: string;
};

// Sample projects - matching the ones from project-development-page.tsx
const sampleProjects: Project[] = [
  {
    id: '1',
    title: 'Guidelines Management System',
    description: 'Section-based editing and chunking system for guidelines. Allows team leads to update guidelines in organized sections, with automatic chunking for AI search integration.',
    status: 'planning',
    category: 'Guidelines',
    icon: <FileText className="h-6 w-6" />
  },
  {
    id: '2',
    title: 'Enhanced Search Features',
    description: 'Multi-document search with advanced filtering, product-specific search, and improved relevance ranking for better AI responses.',
    status: 'proposed',
    category: 'Search',
    icon: <Search className="h-6 w-6" />
  },
  {
    id: '3',
    title: 'Real-time Collaboration',
    description: 'Allow multiple team members to collaborate on guideline updates in real-time with version control and change tracking.',
    status: 'proposed',
    category: 'Collaboration',
    icon: <Users className="h-6 w-6" />
  },
  {
    id: '4',
    title: 'Advanced Product Dashboard',
    description: 'Comprehensive analytics for product performance, user feedback, and AI response quality metrics. Currently being built and available in Response Hub.',
    status: 'in-development',
    category: 'Analytics',
    icon: <BarChart3 className="h-6 w-6" />,
    linkTo: '/response-hub?tab=insights'
  }
];

const statusColors = {
  planning: 'bg-gray-100 text-gray-800 border-gray-200',
  proposed: 'bg-gray-200 text-gray-800 border-gray-300',
  'in-development': 'bg-gray-300 text-gray-900 border-gray-400',
  'on-hold': 'bg-gray-100 text-gray-800 border-gray-200'
};

const statusLabels = {
  planning: 'In Planning',
  proposed: 'Proposed',
  'in-development': 'Currently Being Built',
  'on-hold': 'On Hold'
};

// Diagram/Overview components for each project
const ProjectDiagrams: Record<string, React.ReactNode> = {
  '1': (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Guideline Document</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">Upload or create guideline documents</p>
        </Card>
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <Layers className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Section Editor</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">Organize content into manageable sections</p>
        </Card>
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Auto Chunking</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">Automatic chunking for AI search</p>
        </Card>
      </div>
      <div className="flex items-center justify-center gap-2 text-gray-400">
        <div className="h-px bg-[#3a3a3a] flex-1"></div>
        <ArrowLeft className="h-4 w-4 rotate-90" />
        <div className="h-px bg-[#3a3a3a] flex-1"></div>
      </div>
      <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-4 sm:p-6 text-center">
        <Target className="h-6 w-6 sm:h-8 sm:w-8 text-gray-300 mx-auto mb-2 sm:mb-3" />
        <h3 className="font-semibold text-sm sm:text-base text-gray-100 mb-2">AI Search Integration</h3>
        <p className="text-xs sm:text-sm text-gray-400">Chunked content becomes searchable in chat</p>
      </Card>
    </div>
  ),
  '2': (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <Search className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Multi-Document Search</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">Search across all guidelines simultaneously</p>
        </Card>
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <Target className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Product Filtering</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">Filter by VA, FHA, DSCR, etc.</p>
        </Card>
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Smart Ranking</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">AI-powered relevance scoring</p>
        </Card>
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Advanced Filters</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">Date range, document type, tags</p>
        </Card>
      </div>
    </div>
  ),
  '3': (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <Users className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Team Collaboration</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">Multiple users editing simultaneously</p>
        </Card>
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <GitBranch className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Version Control</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">Track changes and rollback if needed</p>
        </Card>
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <Workflow className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Change Tracking</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">See who changed what and when</p>
        </Card>
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
              <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-100">Real-time Updates</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">See changes as they happen</p>
        </Card>
      </div>
    </div>
  )
};

export default function ProjectIdeaDetailPage() {
  const { user, isLoading } = useAuth();
  const [, params] = useRoute<{ id: string }>('/project-development/project/:id');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#1a1a1a]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-100"></div>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/auth" />;
  }

  if (!(user?.isAdmin || (user as any)?.is_admin)) {
    return <Redirect to="/" />;
  }

  const projectId = params?.id;
  const project = sampleProjects.find(p => p.id === projectId);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center px-4">
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-6 sm:p-8 w-full max-w-md">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-100 mb-2">Project Not Found</h2>
          <p className="text-sm sm:text-base text-gray-400 mb-4">The project you're looking for doesn't exist.</p>
          <Link href="/project-development">
            <Button variant="outline" className="border-[#3a3a3a] text-gray-100 hover:bg-[#3a3a3a] w-full sm:w-auto">
              Back to Projects
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const diagram = ProjectDiagrams[projectId] || (
    <Card className="border border-[#3a3a3a] bg-[#2a2a2a] p-6 sm:p-8 text-center">
      <Lightbulb className="h-10 w-10 sm:h-12 sm:w-12 text-gray-500 mx-auto mb-4" />
      <p className="text-sm sm:text-base text-gray-400">Diagram coming soon...</p>
    </Card>
  );

  return (
    <div 
      className="min-h-screen bg-[#1a1a1a] text-gray-100" 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      {/* Header */}
      <header 
        className="px-4 sm:px-6 h-14 border-b border-[#3a3a3a] bg-[#2a2a2a] sticky top-0 z-50"
        style={{ 
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))'
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center h-full">
          <div className="flex items-center gap-4" style={{ textAlign: 'left' }}>
            <Link href="/project-development">
              <Button 
                variant="ghost" 
                size="sm" 
                className="transition-all duration-200 rounded-md px-3 text-gray-300 hover:text-gray-100 hover:bg-[#3a3a3a] border border-[#3a3a3a] touch-manipulation"
                style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Back to Projects</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </Link>
            <div className="border-l border-[#3a3a3a] pl-2 sm:pl-4 h-10 flex items-center">
              <div className="flex items-center gap-2 text-base sm:text-lg font-semibold text-gray-100" style={{ textAlign: 'left' }}>
                {project.icon}
                <span className="truncate" style={{ textAlign: 'left' }}>{project.title}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8" style={{ textAlign: 'left' }}>
        {/* Project Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row items-start gap-3 mb-3">
                <div className="p-2 sm:p-3 rounded-lg bg-[#3a3a3a] text-gray-300 flex-shrink-0">
                  {project.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-100 mb-2 break-words" style={{ textAlign: 'left' }}>
                    {project.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <Badge className={`${statusColors[project.status]} border text-xs sm:text-sm font-medium`}>
                      {statusLabels[project.status]}
                    </Badge>
                    <span className="text-gray-400 text-xs sm:text-sm">{project.category}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-sm sm:text-base lg:text-lg text-gray-300 leading-relaxed max-w-3xl" style={{ textAlign: 'left', lineHeight: '1.6' }}>
            {project.description}
          </p>
        </div>

        {/* Diagram/Overview Section */}
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a] mb-6 sm:mb-8">
          <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="text-gray-100 text-lg sm:text-xl" style={{ textAlign: 'left' }}>
              Overview & Architecture
            </CardTitle>
            <CardDescription className="text-gray-400 text-sm" style={{ textAlign: 'left' }}>
              Visual representation of how this feature would work
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {diagram}
          </CardContent>
        </Card>

        {/* Features Section */}
        <Card className="border border-[#3a3a3a] bg-[#2a2a2a]">
          <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="text-gray-100 text-lg sm:text-xl" style={{ textAlign: 'left' }}>
              Key Features
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-300" style={{ textAlign: 'left' }}>
              {project.id === '1' && (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Section-based content organization for better structure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Team lead assignment and permissions management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Automatic chunking integration with AI search</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Version history and change tracking</span>
                  </li>
                </>
              )}
              {project.id === '2' && (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Search across multiple documents simultaneously</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Product-specific filtering (VA, FHA, DSCR, etc.)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>AI-powered relevance ranking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Advanced filters for date, type, and tags</span>
                  </li>
                </>
              )}
              {project.id === '3' && (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Real-time collaborative editing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Version control with rollback capabilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Change tracking and audit logs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>Conflict resolution for simultaneous edits</span>
                  </li>
                </>
              )}
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

